import { memo } from 'react';
import { Bank as Landmark, ArrowRight } from '@phosphor-icons/react';
import credit123Logo from '@/assets/123credit-logo.png';
import { trackEvent } from '@/lib/posthog';

interface FinancingCTAProps {
  price: number;
  year: number;
  make: string;
  model?: string;
  trim?: string;
  fuel?: string;
  mileage?: number;
  currency?: string;
  listingId: string;
  monthlyCost?: number;
  title?: string;
}

/**
 * Eligibility for the 123Credit / AutoCompare financing widget.
 * Decizia se ia EXCLUSIV pe preț (în EUR). Anul mașinii nu mai condiționează afișarea.
 *
 *  - sub 1.000 EUR (~5.000 lei): credit de nevoi personale (prag minim 200 lei)
 *  - 1.000 – 10.000 EUR: credit auto (prag minim publicat de 123Credit: 5.000 lei)
 *  - peste 10.000 EUR: leasing
 *
 * AutoCompare acceptă doar `credit` / `leasing` ca `product`, deci ambele tipuri
 * de credit trimit `credit`, iar utilizatorul alege tab-ul corect acolo.
 */
export const getFinancingEligibility = (
  _year?: number,
  _mileageKm?: number,
  priceEur?: number,
): 'leasing' | 'credit' | null => {
  if (!priceEur || priceEur <= 0) return null;
  // Prag orientativ pentru leasing — de confirmat cu echipa AutoCompare / 123Leasing.
  if (priceEur > 10000) return 'leasing';
  return 'credit';
};

export const FinancingCTA = memo(({ price, year, make, model, trim, fuel, mileage, currency, listingId, monthlyCost, title: listingTitle }: FinancingCTAProps) => {
  const priceEur = (currency || 'EUR').toUpperCase() === 'RON'
    ? Math.round(price / 5)
    : Math.round(price);

  const eligibility = getFinancingEligibility(year, mileage, priceEur);
  if (!eligibility) return null;

  const priceRON = (currency || 'EUR').toUpperCase() === 'RON'
    ? Math.round(price)
    : Math.round(price * 5);
  const carTitle = listingTitle || [make, model, trim].filter(Boolean).join(' ');
  const fullTitle = year ? `${carTitle} ${year}` : carTitle;
  const params = new URLSearchParams({
    brand: make || '',
    make: make || '',
    marca: make || '',
    model: model || '',
    trim: trim || '',
    versiune: trim || '',
    year: String(year || ''),
    an: String(year || ''),
    price: String(priceRON),
    pret: String(priceRON),
    currency: 'RON',
    fuel: fuel || '',
    combustibil: fuel || '',
    mileage: String(mileage || ''),
    km: String(mileage || ''),
    listingId,
    product: eligibility,
    title: fullTitle,
    car: fullTitle,
    vehicul: fullTitle,
    masina: fullTitle,
    descriere: fullTitle,
    utm_source: 'roautomarket',
    utm_medium: 'referral',
    utm_campaign: `financing_cta_${eligibility}`,
  });
  const url = `https://www.autocompare.ro/aplica-finantare?${params.toString()}`;
  const rate = monthlyCost ?? Math.round(price / 60);
  const modelLabel = model ? `${make} ${model}` : make;
  const title = `Finanțare pentru acest ${modelLabel} de la ~${rate} €/lună`;
  const ctaLabel = 'Cere ofertă de finanțare';

  return (
    <div className="relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-5 sm:p-6 shadow-lg">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent pointer-events-none" />

      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className="flex-shrink-0 rounded-xl bg-white/15 p-2.5">
            <Landmark className="h-5 w-5" />
          </div>
          <div className="space-y-2 min-w-0 flex-1">
            <h4 className="text-base sm:text-lg font-extrabold tracking-tight leading-snug">
              {title}
            </h4>
            <p className="text-sm text-primary-foreground/80 leading-relaxed inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
              <span>Completează cererea prin <span className="font-semibold text-primary-foreground">AutoCompare.ro</span> și primești opțiuni personalizate prin partenerul nostru</span>
              <span className="inline-flex items-center bg-white rounded-sm px-2 py-1 align-middle">
                <img src={credit123Logo} alt="123Credit" className="h-7 w-auto" loading="lazy" />
              </span>
            </p>
          </div>
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('finance_card_click', { listingId, make, model, year, price, rate, product: eligibility })}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-slate-900 font-extrabold text-sm hover:bg-amber-300 transition-colors shadow-md flex-shrink-0 self-start sm:self-center"
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
      <p className="relative mt-3 text-[11px] text-primary-foreground/60 leading-relaxed">
        Estimare orientativă. Oferta finală depinde de eligibilitate, avans și perioada aleasă.
      </p>
    </div>
  );
});

FinancingCTA.displayName = 'FinancingCTA';

export default FinancingCTA;
